import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import { Feather } from 'lucide-react';
import DarkModeToggle from '@/components/DarkModeToggle';
import LanguageSwitcher from '@/components/LanguageSwitcher';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';

function LoginPage() {
  const { t } = useTranslation();

  const handleLogin = async () => {
    try {
      const { Capacitor } = await import('@capacitor/core');
      if (Capacitor.isNativePlatform()) {
        const { Browser } = await import('@capacitor/browser');
        await Browser.open({ url: `${BACKEND_URL}/api/auth/google?native=true` });
        return;
      }
    } catch {}
    window.location.href = `${BACKEND_URL}/api/auth/google`;
  };

  return (
    <div className="min-h-screen bg-[#F8F5F0] dark:bg-[#1A1816] flex items-center justify-center p-6 relative">
      {/* Background decoration */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute -top-40 -right-40 w-[500px] h-[500px] bg-[#C4B0D0]/8 dark:bg-[#C4B0D0]/3 rounded-full blur-[120px]"></div>
        <div className="absolute bottom-20 -left-40 w-[400px] h-[400px] bg-[#D4C4A0]/8 dark:bg-[#D4C4A0]/3 rounded-full blur-[120px]"></div>
      </div>

      {/* Top Right Controls */}
      <div className="absolute top-5 right-5 flex items-center gap-2">
        <LanguageSwitcher />
        <DarkModeToggle />
      </div>

      <div className="relative max-w-sm w-full text-center">
        {/* Logo */}
        <div className="flex flex-col items-center mb-10">
          <Feather className="w-16 h-16 text-[#6B5A7B] dark:text-[#C4B0D0] mb-5" />
          <h2 className="font-serif text-xl text-[#6B5A7B] dark:text-[#B0A0C0] tracking-wide">The Storyteller</h2>
          <div className="flex items-center mt-3 gap-2">
            <div className="w-10 h-px bg-[#D8D0C4] dark:bg-[#2A2520]"></div>
            <div className="w-1.5 h-1.5 rounded-full bg-[#C4B0D0] dark:bg-[#5C4D6B]"></div>
            <div className="w-10 h-px bg-[#D8D0C4] dark:bg-[#2A2520]"></div>
          </div>
        </div>

        <div className="mb-8">
          <h1 className="font-serif text-2xl sm:text-3xl font-bold text-[#2A2420] dark:text-[#F0EBE4] tracking-tight mb-3">
            {t('auth.loginTitle')}
          </h1>
          <p className="text-base text-[#7C766E] dark:text-[#8C847A] leading-relaxed">
            {t('app.tagline')}
          </p>
        </div>

        <div className="bg-white/60 dark:bg-[#22201C]/60 backdrop-blur-md border border-[#E0D8CC]/50 dark:border-[#2A2520]/50 rounded-2xl p-6 mb-8 text-left space-y-3.5">
          <Feature text={t('auth.features.workspace')} />
          <Feature text={t('auth.features.ignition')} />
          <Feature text={t('auth.features.polish')} />
          <Feature text={t('auth.features.feedback')} />
        </div>

        <Button
          onClick={handleLogin}
          data-testid="google-login-button"
          className="w-full py-6 text-base font-medium bg-[#5C4D6B] hover:bg-[#4A3D58] dark:bg-[#7B6A90] dark:hover:bg-[#6B5A80] text-white rounded-full shadow-[0_8px_30px_-8px_rgba(92,77,107,0.35)] hover:shadow-[0_12px_40px_-8px_rgba(92,77,107,0.45)] hover:-translate-y-0.5 transition-all duration-300"
        >
          <svg className="w-5 h-5 mr-2.5" viewBox="0 0 24 24">
            <path fill="currentColor" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
            <path fill="currentColor" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
            <path fill="currentColor" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
            <path fill="currentColor" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
          </svg>
          {t('auth.login')}
        </Button>

        <p className="text-xs text-[#A09890] dark:text-[#5C5650] mt-6 leading-relaxed">
          {t('auth.termsPolicy')}
        </p>
      </div>
    </div>
  );
}

function Feature({ text }) {
  return (
    <div className="flex items-start gap-3">
      <div className="w-5 h-5 rounded-full bg-[#5C4D6B]/10 dark:bg-[#B0A0C0]/10 flex items-center justify-center flex-shrink-0 mt-0.5">
        <svg className="w-3 h-3 text-[#5C4D6B] dark:text-[#B0A0C0]" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 13l4 4L19 7" />
        </svg>
      </div>
      <p className="text-sm text-[#4A4440] dark:text-[#C0BAB0] leading-relaxed">{text}</p>
    </div>
  );
}

export default LoginPage;
